import React from "react";
import { IoAddCircleOutline, IoTrashBinOutline } from "react-icons/io5";
import Switch from "components/switch";
import { TextInput, NumInput, SelectInput, widths, slugify } from "./ui";

const TIPOS = [
  { value: "texto", label: "Texto" },
  { value: "numerico", label: "Numérico" },
  { value: "selector", label: "Selector" },
  { value: "switch", label: "Switch (Sí/No)" },
];

const emptyFieldForType = (tipo, keep = {}) => {
  const base = { id: keep.id || "", nombre: keep.nombre || "", tipo, requerido: !!keep.requerido };
  if (tipo === "texto") return { ...base, por_defecto: "" };
  if (tipo === "numerico")
    return { ...base, por_defecto: 0, minimo: "", maximo: "", decimales: 0, simbolo: "" };
  if (tipo === "selector") return { ...base, por_defecto: "", opciones: [] };
  if (tipo === "switch") return { ...base, por_defecto: false, campos: [] };
  return base;
};

const SectionLabel = ({ children }) => (
  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
    {children}
  </p>
);

const AddButton = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-max items-center gap-1 rounded-lg border border-dashed border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-all duration-200 hover:border-brand-400 hover:text-brand-500 dark:border-white/20 dark:text-gray-300 dark:hover:border-brand-400 dark:hover:text-brand-400"
  >
    <IoAddCircleOutline className="h-3.5 w-3.5" /> {children}
  </button>
);

const RemoveButton = ({ onClick, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border-2 border-red-200 text-red-500 transition-all duration-200 hover:-translate-y-0.5 hover:bg-red-500 hover:text-white hover:shadow-md dark:border-red-400/40"
  >
    <IoTrashBinOutline />
  </button>
);

// Rows are laid out with flex-wrap and per-field min-widths (not a fixed
// grid): when a row runs out of horizontal space — e.g. deeply nested
// fields inside a switch/selector — items wrap to the next line instead of
// being squeezed into a column too narrow for them, which is what caused
// the delete button to overlap neighboring boxes.
const SelectorOpciones = ({ field, depth, onChange }) => {
  const opciones = field.opciones || [];

  const updateOpcion = (index, patch) => {
    onChange({ opciones: opciones.map((o, i) => (i === index ? { ...o, ...patch } : o)) });
  };
  const removeOpcion = (index) => {
    const removedValor = opciones[index] && opciones[index].valor;
    const next = opciones.filter((_, i) => i !== index);
    const patch = { opciones: next };
    if (field.por_defecto && field.por_defecto === removedValor) patch.por_defecto = "";
    onChange(patch);
  };
  const addOpcion = () => {
    onChange({ opciones: [...opciones, { titulo: "", valor: "", campos: [] }] });
  };

  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <SectionLabel>Opciones del selector</SectionLabel>
        {opciones.length > 0 && (
          <SelectInput
            label="Valor por defecto"
            value={field.por_defecto}
            onChange={(e) => onChange({ por_defecto: e.target.value })}
            options={opciones
              .filter((o) => o.valor)
              .map((o) => ({ value: o.valor, label: o.titulo || o.valor }))}
            className={widths.lg}
          />
        )}
      </div>

      {opciones.map((opcion, index) => (
        <div key={index} className="rounded-lg bg-gray-50 p-2.5 dark:bg-white/5">
          <div className="flex flex-wrap items-end gap-2">
            <TextInput
              label="Título"
              value={opcion.titulo}
              onChange={(e) => updateOpcion(index, { titulo: e.target.value })}
              onBlur={() => {
                if (!opcion.valor && opcion.titulo) {
                  updateOpcion(index, { valor: slugify(opcion.titulo) });
                }
              }}
              placeholder="Ej: Automático"
              className={widths.grow}
            />
            <TextInput
              label="Valor"
              value={opcion.valor}
              onChange={(e) => updateOpcion(index, { valor: slugify(e.target.value) })}
              placeholder="automatico"
              className={widths.grow}
            />
            <RemoveButton onClick={() => removeOpcion(index)} title="Eliminar opción" />
          </div>

          <div className="mt-2">
            <SectionLabel>Campos visibles al elegir esta opción</SectionLabel>
            <FieldConfigEditor
              fields={opcion.campos || []}
              depth={depth + 1}
              onChange={(campos) => updateOpcion(index, { campos })}
            />
          </div>
        </div>
      ))}

      <AddButton onClick={addOpcion}>Agregar opción</AddButton>
    </div>
  );
};

const FieldRow = ({ field, depth, onChange, onReplace, onRemove }) => {
  const handleTipoChange = (tipo) => {
    onReplace(emptyFieldForType(tipo, field));
  };

  return (
    <div
      className={`rounded-xl border border-gray-200 bg-white/60 p-2.5 shadow-sm transition-all duration-200 dark:border-white/10 dark:bg-white/5 ${
        depth > 0 ? "border-l-4 border-l-brand-300 dark:border-l-brand-400" : ""
      }`}
    >
      <div className="flex flex-wrap items-end gap-2">
        <TextInput
          label="Nombre"
          value={field.nombre}
          onChange={(e) => onChange({ nombre: e.target.value })}
          onBlur={() => {
            if (!field.id && field.nombre) onChange({ id: slugify(field.nombre) });
          }}
          placeholder="Ej: SSID WIFI"
          className={widths.grow}
        />
        <TextInput
          label="ID"
          value={field.id}
          onChange={(e) => onChange({ id: slugify(e.target.value) })}
          placeholder="ssid"
          className={widths.lg}
        />
        <SelectInput
          label="Tipo"
          value={field.tipo}
          onChange={(e) => handleTipoChange(e.target.value)}
          options={TIPOS}
          className={widths.lg}
        />
        <label className="flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">
          <Switch
            checked={!!field.requerido}
            onChange={(e) => onChange({ requerido: e.target.checked })}
          />
          Requerido
        </label>
        <RemoveButton onClick={onRemove} title="Eliminar campo" />
      </div>

      {field.tipo === "texto" && (
        <div className="mt-2">
          <TextInput
            label="Valor por defecto"
            value={field.por_defecto}
            onChange={(e) => onChange({ por_defecto: e.target.value })}
          />
        </div>
      )}

      {field.tipo === "numerico" && (
        <div className="mt-2 flex flex-wrap gap-2">
          <NumInput label="Mínimo" value={field.minimo} onChange={(e) => onChange({ minimo: e.target.value })} className={widths.sm} />
          <NumInput label="Máximo" value={field.maximo} onChange={(e) => onChange({ maximo: e.target.value })} className={widths.sm} />
          <NumInput
            label="Decimales"
            value={field.decimales}
            onChange={(e) => onChange({ decimales: e.target.value })}
            className={widths.sm}
          />
          <TextInput
            label="Símbolo/Unidad"
            value={field.simbolo}
            onChange={(e) => onChange({ simbolo: e.target.value })}
            placeholder="s, %, °C..."
            className={widths.md}
          />
          <NumInput
            label="Valor por defecto"
            value={field.por_defecto}
            onChange={(e) => onChange({ por_defecto: e.target.value })}
            className={widths.md}
          />
        </div>
      )}

      {field.tipo === "switch" && (
        <div className="mt-2">
          <label className="mb-2 flex h-9 items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
            <Switch
              checked={!!field.por_defecto}
              onChange={(e) => onChange({ por_defecto: e.target.checked })}
            />
            Activado por defecto
          </label>
          <div className="rounded-lg bg-gray-50 p-2.5 dark:bg-white/5">
            <SectionLabel>Campos visibles cuando está activado</SectionLabel>
            <FieldConfigEditor
              fields={field.campos || []}
              depth={depth + 1}
              onChange={(campos) => onChange({ campos })}
            />
          </div>
        </div>
      )}

      {field.tipo === "selector" && <SelectorOpciones field={field} depth={depth} onChange={onChange} />}
    </div>
  );
};

const FieldConfigEditor = ({ fields = [], onChange, depth = 0 }) => {
  const updateField = (index, patch) => {
    onChange(fields.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  };
  const replaceField = (index, field) => {
    onChange(fields.map((f, i) => (i === index ? field : f)));
  };
  const removeField = (index) => {
    onChange(fields.filter((_, i) => i !== index));
  };
  const addField = () => {
    onChange([...fields, emptyFieldForType("texto")]);
  };

  return (
    <div className="flex flex-col gap-2">
      {fields.length === 0 && depth === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Sin campos de configuración. Estos son los ajustes que se muestran al configurar un dispositivo (ej: WiFi, intervalo de medición).
        </p>
      )}

      {fields.map((field, index) => (
        <FieldRow
          key={index}
          field={field}
          depth={depth}
          onChange={(patch) => updateField(index, patch)}
          onReplace={(f) => replaceField(index, f)}
          onRemove={() => removeField(index)}
        />
      ))}

      <AddButton onClick={addField}>Agregar campo</AddButton>
    </div>
  );
};

export default FieldConfigEditor;
